<script lang="ts">
  /**
   * T28 — PrintableReport.svelte
   * Renderiza o ReportModelT27 em formato print-friendly.
   * CSS com @media print para impressão/PDF via window.print().
   */
  import type { ReportModelT27, ReportSectionT27 } from '$core/report/report.types';
  import { formatScore } from '$core/format/numberFormat';

  export let report: ReportModelT27;

  /** Tipos do conteúdo das seções */
  type CoverContent = {
    period?: string;
    analyzedEmployees?: number;
    scoreTotal?: number;
    scoreBand?: string;
    limitationNote?: string;
  };

  type ExecutiveSummaryContent = {
    highlights?: Array<{
      code: string;
      label: string;
      formattedValue: string;
      explanation: string;
      assumption?: string;
      category: string;
    }>;
  };

  type BaseContent = {
    analyzedEmployees?: number;
    analyzedDays?: number;
    totalPunches?: number;
    completeJourneys?: number;
    pendingJourneys?: number;
  };

  type MetricsContent = {
    cost?: {
      excessMinutes?: number;
      excessMinutesPerEmployee?: number;
    };
    absence?: {
      shortJourneys?: number;
      shortJourneyMinutes?: number;
      unjustifiedAbsences?: number;
      missingExpectedDays?: number;
    };
    rework?: {
      manualPunches?: number;
      disregardedPunches?: number;
      journeysWithAdjustments?: number;
      adjustmentRate?: number;
    };
    risk?: {
      totalRiskSignals?: number;
      inconsistentPairsCount?: number;
      excessiveJourneysCount?: number;
      shortIntradayBreaksCount?: number;
      shortInterjourneyBreaksCount?: number;
      potentialBritishPointCount?: number;
      documentIntegrityCount?: number;
    };
  };

  type ScoreContent = {
    total?: number;
    band?: string;
    bandLabel?: string;
    dimensions?: Array<{
      code: string;
      label: string;
      value: number;
      weight: number;
      rationale: string;
      underdetermined: boolean;
    }>;
    pilot?: boolean;
  };

  type RecommendationsContent = {
    recommendations?: Array<{
      code: string;
      title?: string;
      body?: string;
      priority: string;
      nextStep?: string;
    }>;
  };

  type NextStepsContent = {
    steps?: Array<{
      code: string;
      nextStep?: string;
      priority: string;
    }>;
  };

  type LimitationsContent = {
    confidence?: string;
    hasBlocking?: boolean;
    blockingCount?: number;
    errorCount?: number;
    warningCount?: number;
    issues?: Array<{
      code: string;
      severity: string;
      message: string;
      recommendation?: string;
    }>;
  };

  type AssumptionsContent = {
    items?: Array<{ label: string; value: string }>;
  };

  function asRecord(content: unknown): Record<string, unknown> {
    return (content as Record<string, unknown>) ?? {};
  }

  function getCover(section: ReportSectionT27): CoverContent {
    return asRecord(section.content) as CoverContent;
  }
  function getExecSummary(section: ReportSectionT27): ExecutiveSummaryContent {
    return asRecord(section.content) as ExecutiveSummaryContent;
  }
  function getBase(section: ReportSectionT27): BaseContent {
    return asRecord(section.content) as BaseContent;
  }
  function getMetrics(section: ReportSectionT27): MetricsContent {
    return asRecord(section.content) as MetricsContent;
  }
  function getScore(section: ReportSectionT27): ScoreContent {
    return asRecord(section.content) as ScoreContent;
  }
  function getRecs(section: ReportSectionT27): RecommendationsContent {
    return asRecord(section.content) as RecommendationsContent;
  }
  function getNextSteps(section: ReportSectionT27): NextStepsContent {
    return asRecord(section.content) as NextStepsContent;
  }
  function getLimitations(section: ReportSectionT27): LimitationsContent {
    return asRecord(section.content) as LimitationsContent;
  }
  function getAssumptions(section: ReportSectionT27): AssumptionsContent {
    return asRecord(section.content) as AssumptionsContent;
  }
</script>

<article class="printable-report">
  <!-- Aviso piloto sempre em destaque -->
  <div class="printable-report__pilot-notice">
    <strong>Aviso piloto:</strong> {report.pilotNotice}
  </div>

  {#each report.sections as section (section.kind)}
    {#if section.kind === 'cover'}
      {@const c = getCover(section)}
      <div class="printable-report__cover">
        <h1 class="printable-report__cover-title">{section.title}</h1>
        <p class="printable-report__cover-meta">Período: {c.period ?? report.periodLabel}</p>
        <p class="printable-report__cover-meta">
          Colaboradores analisados: <strong>{c.analyzedEmployees}</strong>
        </p>
        <div class="printable-report__cover-score">
          <span class="printable-report__score-value">{c.scoreTotal !== undefined ? formatScore(c.scoreTotal) : '—'}</span>
          <span class="printable-report__score-denom">/100</span>
          <span class="printable-report__score-band">{c.scoreBand}</span>
        </div>
        {#if c.limitationNote}
          <p class="printable-report__cover-meta">{c.limitationNote}</p>
        {/if}
        <p class="printable-report__cover-meta">Gerado em: {report.generatedAt}</p>
      </div>

    {:else if section.kind === 'executive-summary'}
      {@const c = getExecSummary(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <div class="printable-report__highlights">
          {#each c.highlights ?? [] as hl (hl.code)}
            <div
              class="printable-report__highlight printable-report__highlight--{hl.category}"
            >
              <span class="printable-report__hl-label">{hl.label}</span>
              <span class="printable-report__hl-value">{hl.formattedValue}</span>
              <span class="printable-report__hl-explanation">{hl.explanation}</span>
              {#if hl.assumption}
                <span class="printable-report__hl-assumption">{hl.assumption}</span>
              {/if}
            </div>
          {/each}
        </div>
      </section>

    {:else if section.kind === 'base'}
      {@const c = getBase(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <table class="printable-report__table">
          <thead>
            <tr><th>Métrica</th><th>Valor</th></tr>
          </thead>
          <tbody>
            <tr><td>Colaboradores analisados</td><td>{c.analyzedEmployees}</td></tr>
            <tr><td>Dias analisados</td><td>{c.analyzedDays}</td></tr>
            <tr><td>Total de marcações</td><td>{c.totalPunches}</td></tr>
            <tr><td>Jornadas completas</td><td>{c.completeJourneys}</td></tr>
            <tr><td>Jornadas pendentes</td><td>{c.pendingJourneys}</td></tr>
          </tbody>
        </table>
      </section>

    {:else if section.kind === 'metrics'}
      {@const c = getMetrics(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <h3 class="printable-report__subsection-title">Custo</h3>
        <table class="printable-report__table">
          <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Minutos excedentes</td><td>{c.cost?.excessMinutes}</td></tr>
            <tr><td>Min. excedentes / colaborador</td><td>{c.cost?.excessMinutesPerEmployee}</td></tr>
          </tbody>
        </table>
        <h3 class="printable-report__subsection-title">Ausências</h3>
        <table class="printable-report__table">
          <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Jornadas mais curtas que a escala</td><td>{c.absence?.shortJourneys ?? 0}</td></tr>
            <tr><td>Minutos de jornada mais curta</td><td>{c.absence?.shortJourneyMinutes ?? 0}</td></tr>
            <tr><td>Ausências injustificadas</td><td>{c.absence?.unjustifiedAbsences}</td></tr>
            <tr><td>Dias esperados ausentes</td><td>{c.absence?.missingExpectedDays}</td></tr>
          </tbody>
        </table>
        <h3 class="printable-report__subsection-title">Retrabalho</h3>
        <table class="printable-report__table">
          <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Marcações manuais</td><td>{c.rework?.manualPunches}</td></tr>
            <tr><td>Marcações desconsideradas</td><td>{c.rework?.disregardedPunches}</td></tr>
            <tr><td>Jornadas com ajustes</td><td>{c.rework?.journeysWithAdjustments}</td></tr>
            <tr><td>Taxa de ajuste</td><td>{c.rework?.adjustmentRate}</td></tr>
          </tbody>
        </table>
        <h3 class="printable-report__subsection-title">Risco</h3>
        <table class="printable-report__table">
          <thead><tr><th>Métrica</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Sinais de risco total</td><td>{c.risk?.totalRiskSignals}</td></tr>
            <tr><td>Pares inconsistentes</td><td>{c.risk?.inconsistentPairsCount}</td></tr>
            <tr><td>Jornadas excessivas</td><td>{c.risk?.excessiveJourneysCount}</td></tr>
            <tr><td>Intervalos intrajornada curtos</td><td>{c.risk?.shortIntradayBreaksCount}</td></tr>
            <tr><td>Interjornadas curtas</td><td>{c.risk?.shortInterjourneyBreaksCount}</td></tr>
            <tr><td>Ponto britânico potencial</td><td>{c.risk?.potentialBritishPointCount}</td></tr>
            <tr><td>Integridade documental</td><td>{c.risk?.documentIntegrityCount}</td></tr>
          </tbody>
        </table>
      </section>

    {:else if section.kind === 'score'}
      {@const c = getScore(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <div class="printable-report__score-summary">
          <span class="printable-report__score-big">{c.total}</span>
          <span class="printable-report__score-denom">/100</span>
          <span class="printable-report__score-band-chip">{c.bandLabel}</span>
        </div>
        {#if c.dimensions && c.dimensions.length > 0}
          <table class="printable-report__table">
            <thead>
              <tr>
                <th>Dimensão</th>
                <th>Pontos</th>
                <th>Peso</th>
                <th>Racional</th>
              </tr>
            </thead>
            <tbody>
              {#each c.dimensions as dim (dim.code)}
                <tr>
                  <td>{dim.label}</td>
                  <td>{dim.value}</td>
                  <td>{(dim.weight * 100).toFixed(0)}%</td>
                  <td class="printable-report__td-rationale">{dim.rationale}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </section>

    {:else if section.kind === 'recommendations'}
      {@const c = getRecs(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        {#each c.recommendations ?? [] as rec (rec.code)}
          <div class="printable-report__rec">
            <span class="printable-report__rec-priority printable-report__rec-priority--{rec.priority}">
              {rec.priority}
            </span>
            {#if rec.title}
              <p class="printable-report__rec-title">{rec.title}</p>
            {/if}
            {#if rec.body}
              <p class="printable-report__rec-body">{rec.body}</p>
            {/if}
            {#if rec.nextStep}
              <p class="printable-report__rec-body">
                <strong>Próximo passo:</strong> {rec.nextStep}
              </p>
            {/if}
          </div>
        {/each}
      </section>

    {:else if section.kind === 'next-steps'}
      {@const c = getNextSteps(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        {#each c.steps ?? [] as step, i (step.code)}
          <div class="printable-report__rec">
            <span class="printable-report__rec-priority printable-report__rec-priority--{step.priority}">
              {i + 1}
            </span>
            {#if step.nextStep}
              <p class="printable-report__rec-body">{step.nextStep}</p>
            {/if}
          </div>
        {/each}
      </section>

    {:else if section.kind === 'limitations'}
      {@const c = getLimitations(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <table class="printable-report__table" style="margin-bottom: 12px;">
          <thead><tr><th>Indicador</th><th>Valor</th></tr></thead>
          <tbody>
            <tr><td>Confiabilidade</td><td>{c.confidence}</td></tr>
            <tr><td>Issues bloqueantes</td><td>{c.blockingCount}</td></tr>
            <tr><td>Erros</td><td>{c.errorCount}</td></tr>
            <tr><td>Avisos</td><td>{c.warningCount}</td></tr>
          </tbody>
        </table>
        {#each c.issues ?? [] as issue, idx (issue.code + '_' + idx)}
          <div class="printable-report__issue printable-report__issue--{issue.severity}">
            <strong>[{issue.severity}] {issue.code}</strong>: {issue.message}
            {#if issue.recommendation}
              <p class="printable-report__issue-rec">{issue.recommendation}</p>
            {/if}
          </div>
        {/each}
      </section>

    {:else if section.kind === 'assumptions'}
      {@const c = getAssumptions(section)}
      <section class="printable-report__section">
        <h2 class="printable-report__section-title">{section.title}</h2>
        {#if section.intro}
          <p class="printable-report__section-intro">{section.intro}</p>
        {/if}
        <table class="printable-report__table">
          <thead><tr><th>Premissa</th><th>Valor</th></tr></thead>
          <tbody>
            {#each c.items ?? [] as item (item.label)}
              <tr>
                <td>{item.label}</td>
                <td>{item.value}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </section>
    {/if}
  {/each}

  <footer class="printable-report__footer">
    Diagnóstico de Jornada — Versão {report.version} — Gerado em {report.generatedAt}
  </footer>
</article>

<style>
  /* ===========================================================
     Layout base
     =========================================================== */
  .printable-report {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px;
    color: var(--ptt-gray-300, #323232);
    background: var(--ptt-white, #ffffff);
    line-height: 1.6;
    max-width: 860px;
    margin: 0 auto;
    padding: var(--ptt-space-6, 1.5rem);
  }

  /* Aviso piloto */
  .printable-report__pilot-notice {
    background: var(--ptt-yellow-10, #fef3cf);
    border-left: 4px solid var(--ptt-yellow-100, #fccc31);
    padding: 10px 14px;
    margin-bottom: 20px;
    font-size: 0.875rem;
    color: var(--ptt-gray-300, #323232);
    border-radius: 4px;
  }

  /* Capa */
  .printable-report__cover {
    background: var(--ptt-gray-10, #f5f5f5);
    border-radius: 6px;
    padding: 20px 24px;
    margin-bottom: 24px;
  }

  .printable-report__cover-title {
    font-size: 1.75rem;
    color: var(--ptt-black, #000000);
    margin: 0 0 8px;
  }

  .printable-report__cover-meta {
    color: var(--ptt-gray-100, #7d7d7d);
    font-size: 0.875rem;
    margin: 4px 0;
  }

  .printable-report__cover-score {
    margin-top: 12px;
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .printable-report__score-value {
    font-size: 2rem;
    font-weight: 700;
    color: var(--ptt-purple-100, #7f3d7d);
  }

  .printable-report__score-denom {
    font-size: 1rem;
    color: var(--ptt-gray-100, #7d7d7d);
  }

  .printable-report__score-band {
    font-size: 0.875rem;
    color: var(--ptt-gray-200, #565656);
    background: var(--ptt-gray-20, #e7e7e7);
    padding: 2px 10px;
    border-radius: 100px;
  }

  /* Seções */
  .printable-report__section {
    margin-bottom: 28px;
  }

  .printable-report__section-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--ptt-gray-300, #323232);
    margin: 0 0 6px;
    padding-bottom: 4px;
    border-bottom: 2px solid var(--ptt-gray-20, #e7e7e7);
  }

  .printable-report__section-intro {
    font-size: 0.875rem;
    color: var(--ptt-gray-100, #7d7d7d);
    margin-bottom: 10px;
  }

  .printable-report__subsection-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--ptt-gray-200, #565656);
    margin: 14px 0 6px;
  }

  /* Highlights executive summary */
  .printable-report__highlights {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    margin-top: 10px;
  }

  .printable-report__highlight {
    background: var(--ptt-gray-10, #f5f5f5);
    border-radius: 4px;
    padding: 10px 12px;
    border-top: 3px solid var(--ptt-purple-100, #7f3d7d);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .printable-report__highlight--cost       { border-top-color: var(--ptt-orange-100, #fb7224); }
  .printable-report__highlight--rework     { border-top-color: var(--ptt-purple-100, #7f3d7d); }
  .printable-report__highlight--risk       { border-top-color: #e64bc8; }
  .printable-report__highlight--score      { border-top-color: var(--ptt-yellow-100, #fccc31); }
  .printable-report__highlight--visibility { border-top-color: var(--ptt-blue-100, #2062e1); }

  .printable-report__hl-label {
    font-size: 0.7rem;
    color: var(--ptt-gray-100, #7d7d7d);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .printable-report__hl-value {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--ptt-gray-300, #323232);
  }

  .printable-report__hl-explanation {
    font-size: 0.75rem;
    color: var(--ptt-gray-200, #565656);
  }

  .printable-report__hl-assumption {
    font-size: 0.7rem;
    color: var(--ptt-gray-100, #7d7d7d);
    font-style: italic;
  }

  /* Tabelas */
  .printable-report__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    margin-bottom: 8px;
  }

  .printable-report__table th {
    text-align: left;
    padding: 6px 8px;
    background: var(--ptt-gray-10, #f5f5f5);
    border-bottom: 2px solid var(--ptt-gray-20, #e7e7e7);
    color: var(--ptt-gray-200, #565656);
    font-weight: 600;
  }

  .printable-report__table td {
    padding: 6px 8px;
    border-bottom: 1px solid var(--ptt-gray-20, #e7e7e7);
    color: var(--ptt-gray-300, #323232);
  }

  .printable-report__table tr:last-child td {
    border-bottom: none;
  }

  .printable-report__td-rationale {
    font-size: 0.8rem;
    color: var(--ptt-gray-100, #7d7d7d);
  }

  /* Score summary */
  .printable-report__score-summary {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 12px;
  }

  .printable-report__score-big {
    font-size: 3rem;
    font-weight: 700;
    color: var(--ptt-purple-100, #7f3d7d);
  }

  .printable-report__score-band-chip {
    font-size: 0.875rem;
    background: var(--ptt-yellow-10, #fef3cf);
    color: var(--ptt-gray-300, #323232);
    padding: 2px 10px;
    border-radius: 100px;
  }

  /* Recomendações */
  .printable-report__rec {
    margin-bottom: 10px;
    padding: 10px 12px;
    background: var(--ptt-gray-10, #f5f5f5);
    border-radius: 4px;
  }

  .printable-report__rec-priority {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 2px 6px;
    border-radius: 4px;
    color: #ffffff;
    display: inline-block;
    margin-bottom: 4px;
  }

  .printable-report__rec-priority--critical { background: var(--ptt-gray-300, #323232); }
  .printable-report__rec-priority--high     { background: var(--ptt-purple-100, #7f3d7d); }
  .printable-report__rec-priority--medium   { background: var(--ptt-gray-100, #7d7d7d); }
  .printable-report__rec-priority--low      { background: var(--ptt-gray-20, #e7e7e7); color: var(--ptt-gray-300, #323232); }

  .printable-report__rec-title {
    font-weight: 600;
    color: var(--ptt-gray-300, #323232);
    margin: 0 0 2px;
  }

  .printable-report__rec-body {
    font-size: 0.875rem;
    color: var(--ptt-gray-200, #565656);
    margin: 2px 0 0;
  }

  /* Limitações */
  .printable-report__issue {
    margin-bottom: 6px;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 0.875rem;
  }

  .printable-report__issue--blocking {
    background: var(--ptt-yellow-10, #fef3cf);
    border-left: 4px solid var(--ptt-orange-100, #fb7224);
  }

  .printable-report__issue--error {
    background: var(--ptt-gray-10, #f5f5f5);
    border-left: 4px solid var(--ptt-gray-100, #7d7d7d);
  }

  .printable-report__issue--warning {
    background: var(--ptt-gray-10, #f5f5f5);
    border-left: 4px solid var(--ptt-gray-20, #e7e7e7);
  }

  .printable-report__issue-rec {
    font-size: 0.8rem;
    color: var(--ptt-gray-100, #7d7d7d);
    margin: 4px 0 0;
  }

  /* Footer */
  .printable-report__footer {
    margin-top: 40px;
    padding-top: 12px;
    border-top: 1px solid var(--ptt-gray-20, #e7e7e7);
    font-size: 0.75rem;
    color: var(--ptt-gray-100, #7d7d7d);
    text-align: center;
  }

  /* ===========================================================
     @media print — impressão / PDF
     =========================================================== */
  @media print {
    .printable-report {
      padding: 0;
      font-size: 12px;
      max-width: 100%;
    }

    .printable-report__section {
      page-break-inside: avoid;
    }

    .printable-report__section-title {
      page-break-after: avoid;
    }

    .printable-report__cover {
      page-break-after: always;
    }

    .printable-report__highlights {
      grid-template-columns: repeat(3, 1fr);
    }

    /* Preserva cores em impressão */
    .printable-report__pilot-notice {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .printable-report__highlight {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .printable-report__rec-priority {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* Força cores grayscale-friendly para impressoras P&B */
    .printable-report__score-big,
    .printable-report__score-value {
      color: #000000;
    }

    .printable-report__score-band-chip,
    .printable-report__score-band {
      border: 1px solid #000000;
      background: transparent;
    }
  }
</style>
